import React from 'react';

const STATUS_COLOR = {
  available:   { bg: 'var(--color-success-50)', color: 'var(--color-success-500)', label: 'Available' },
  provisioned: { bg: 'var(--brand-soft)', color: 'var(--brand)', label: 'In Use' },
  maintenance: { bg: 'var(--color-warning-50)', color: 'var(--color-warning-500)', label: 'Maintenance' },
  running:     { bg: 'var(--color-success-50)', color: 'var(--color-success-500)', label: 'Running' },
  stopped:     { bg: 'var(--bg)', color: 'var(--muted)', label: 'Stopped' },
  pending:     { bg: 'var(--accent-soft)', color: 'var(--accent)', label: 'Pending' },
  terminated:  { bg: 'var(--color-danger-50)', color: 'var(--color-danger-500)', label: 'Terminated' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLOR[status] || { bg: 'var(--bg)', color: 'var(--muted)', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px',
      borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{s.label}</span>
  );
}

export function GpuRequests({
  gpuRequests,
  gpuServers,
  provisionResult,
  provisioningReqId,
  podStatus,
  onNewRequest,
  onApproveRequest,
  onProvisionRequest,
  onCheckPodStatus,
  onReProvision,
  onDismissProvisionResult,
  isAdmin,
  canCreate,
  canEdit,
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-2)' }}>GPU Resource Requests</div>
        {canCreate && (
        <button className="btn btn-primary btn-sm" onClick={onNewRequest}>
          + New Request
        </button>
        )}
      </div>

      {/* Provision Result */}
      {provisionResult && (
        <div className={`banner ${provisionResult.error ? 'danger' : 'success'}`} style={{ marginBottom: 16 }}>
          {provisionResult.error ? (
            <div>Provisioning failed: {provisionResult.error}</div>
          ) : (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Instance provisioned successfully!</div>
              <div style={{ fontFamily: 'monospace', fontSize: 13, background: '#1a202c', color: '#9ae6b4', padding: 12, borderRadius: 6 }}>
                <div>SSH: <strong>{provisionResult.connect_cmd}</strong></div>
                <div>Password: <strong>{provisionResult.ssh_password}</strong></div>
                <div>Jupyter: http://{provisionResult.ssh_host}:{provisionResult.jupyter_port}</div>
                <div>GPUs: {provisionResult.gpu_count}× | Image: {provisionResult.docker_image}</div>
                <div>Expires: {provisionResult.expires_at ? new Date(provisionResult.expires_at).toLocaleDateString() : '—'}</div>
              </div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onDismissProvisionResult}>Dismiss</button>
            </div>
          )}
        </div>
      )}

      {gpuRequests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted-2)' }}>
          <div style={{ fontSize: 36 }}>📋</div>
          <div style={{ marginTop: 8 }}>No GPU requests yet</div>
          {canCreate && (
          <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={onNewRequest}>Submit a Request</button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {gpuRequests.map(req => (
            <div key={req.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-2)' }}>{req.project_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {req.officer_name} · {req.vertical} · {req.email}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Submitted: {req.created_at ? new Date(req.created_at).toLocaleString() : '—'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <StatusBadge status={req.status} />
                  {isAdmin && canEdit && req.status === 'pending' && (
                    <>
                      <button className="btn btn-tinted-success btn-sm"
                        onClick={() => onApproveRequest(req.id, 'approved')}>Approve</button>
                      <button className="btn btn-tinted-danger btn-sm"
                        onClick={() => onApproveRequest(req.id, 'rejected')}>Reject</button>
                    </>
                  )}
                  {isAdmin && canCreate && req.status === 'approved' && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select className="select" style={{ flex: 1 }}
                        defaultValue="" id={`gs-${req.id}`}>
                        <option value="">Select GPU Server</option>
                        {gpuServers.map(gs => (
                          <option key={gs.id} value={gs.id}>{gs.server_name} ({gs.gpu_model})</option>
                        ))}
                      </select>
                      <button className="btn btn-primary btn-sm"
                        disabled={provisioningReqId === req.id}
                        onClick={() => {
                          const sel = document.getElementById(`gs-${req.id}`);
                          onProvisionRequest(req.id, sel?.value ? parseInt(sel.value) : null);
                        }}>
                        {provisioningReqId === req.id ? 'Provisioning...' : 'Provision'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Requirements summary */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {[
                  req.gpu_count && `${req.gpu_count}× GPU`,
                  req.gpu_memory_gb && `${req.gpu_memory_gb}GB VRAM`,
                  req.gpu_usage_type,
                  req.vcpu_requested && `${req.vcpu_requested} vCPU`,
                  req.ram_requested_gb && `${req.ram_requested_gb}GB RAM`,
                  req.storage_tb && `${req.storage_tb}TB Storage`,
                  req.nvme_storage_tb && `${req.nvme_storage_tb}TB NVMe`,
                  req.subdomain && `🌐 ${req.subdomain}.kube.sarv.com:${req.app_port}`,
                  req.os_version,
                ].filter(Boolean).map((t, i) => (
                  <span key={i} className="chip">{t}</span>
                ))}
                {req.llm_model && req.llm_model !== 'none' && (
                  <span className="badge success">
                    🤖 {req.llm_model.split('/').pop()} ({req.llm_quantization || 'int8'})
                  </span>
                )}
                {(req.frameworks||[]).map(f => (
                  <span key={f} className="chip">{f}</span>
                ))}
                {(req.databases||[]).map(d => (
                  <span key={d} className="chip accent">{d}</span>
                ))}
              </div>
              {req.project_description && (
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 8, borderTop: '1px solid var(--bg)', paddingTop: 8 }}>
                  {req.project_description}
                </div>
              )}
              {req.allocation_id && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: 'var(--color-success-500)' }}>Allocation #{req.allocation_id} provisioned</span>
                  {isAdmin && (
                    <>
                      <button className="btn btn-tinted-brand btn-sm"
                        onClick={() => onCheckPodStatus(req.id)}>📋 Pod Status</button>
                      {canCreate && (
                      <button className="btn btn-tinted-brand btn-sm"
                        onClick={() => onReProvision(req.id)}>↻ Re-provision</button>
                      )}
                    </>
                  )}
                </div>
              )}
              {podStatus[req.id] && (
                <div style={{ marginTop: 8, background: '#1a202c', borderRadius: 6, padding: 10 }}>
                  <div style={{ color: '#68d391', fontSize: 12, marginBottom: 4 }}>
                    Pod: <strong>{podStatus[req.id].status}</strong>
                    {!podStatus[req.id].agent && <span style={{ color: '#fc8181', marginLeft: 8 }}>(agent offline)</span>}
                  </div>
                  <pre style={{ color: '#e2e8f0', fontSize: 11, margin: 0, whiteSpace: 'pre-wrap', maxHeight: 150, overflowY: 'auto' }}>
                    {podStatus[req.id].logs || 'No logs yet'}
                  </pre>
                </div>
              )}
              {isAdmin && canCreate && req.status === 'provisioned' && !req.allocation_id && (
                <div style={{ marginTop: 8 }}>
                  <button className="btn btn-tinted-brand btn-sm"
                    onClick={() => onReProvision(req.id)}>↻ Re-provision</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
